import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Clock, Search, ArrowRight } from "lucide-react";

export default function PublicTracker() {
  const [code, setCode] = useState("");
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    setTracker(null);
    try {
      const { data } = await api.get(`/public/trackers/${code.trim().toUpperCase()}`);
      setTracker(data);
    } catch (e) {
      setError("No tracker found with that tracking code. Please verify the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[800px] mx-auto px-6 py-16 text-left min-h-[75vh]">
      <div className="label-eyebrow flex items-center gap-1.5 text-[var(--brand-primary)]">
        <Clock className="w-4 h-4" /> Live Progression Lookup
      </div>
      <h1 className="font-display font-black text-4xl sm:text-5xl mt-3 tracking-tight leading-tight">
        Track your account.
      </h1>
      <p className="text-sm text-[#5C5C5C] mt-2 max-w-xl">
        Enter your unique tracking code below to view real-time progression logs, custom payment statuses, or recruiter review updates on your account.
      </p>

      {/* Code Search bar */}
      <form onSubmit={handleSearch} className="mt-8 flex gap-2 border border-[#0F0F0F] bg-[#F3F2EB] p-2">
        <div className="flex-1 flex items-center gap-2 pl-2">
          <Search className="w-4 h-4 text-[#5C5C5C] shrink-0" />
          <input
            data-testid="public-tracker-input"
            type="text"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. TRK-ABC-123"
            className="w-full bg-transparent text-sm focus:outline-none text-[#0F0F0F] font-mono tracking-widest uppercase"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="public-tracker-search-btn"
          className="btn-terra py-2.5 px-6 text-sm font-semibold uppercase tracking-wider"
        >
          {loading ? "Searching..." : "Search Code"}
        </button>
      </form>

      {error && (
        <div data-testid="public-tracker-error" className="mt-4 text-sm text-[var(--brand-primary)] font-medium">
          {error}
        </div>
      )}

      {/* Tracker results rendering */}
      {tracker && (
        <div data-testid="public-tracker-result-card" className="mt-8 border border-[#E2E0D8] bg-[#FDFCF8] p-6 shadow-sm">
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <span className="bg-[#0F0F0F] text-[#FDFCF8] text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1">
              {tracker.status}
            </span>
            <span className="font-mono text-xs font-bold text-[#5C5C5C]">
              {tracker.tracking_code}
            </span>
          </div>
          
          <h2 className="font-display font-black text-2xl">{tracker.title}</h2>
          <div className="text-xs text-[#5C5C5C] mt-1">
            Initialized on {new Date(tracker.created_at).toLocaleDateString()}
          </div>

          <div className="mt-6 border-t border-[#E2E0D8] pt-4 space-y-4">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C5C] mb-2 pl-3">
              Progress Updates History ({tracker.updates?.length || 0})
            </div>
            
            <div className="space-y-4 pl-3 border-l-2 border-[#0F0F0F]">
              {(tracker.updates || []).map((up, idx) => (
                <div key={up.update_id || idx} className="text-sm leading-snug">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] uppercase font-bold tracking-widest px-2 py-0.5 bg-[#F3F2EB] border border-[#E2E0D8]">
                      {up.status}
                    </span>
                    <span className="text-[10px] text-[#5C5C5C]">
                      {new Date(up.at).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-1 text-[#3a3a3a] font-medium leading-relaxed">
                    {up.message}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
